/**
 * MemeLaunch Fee Configuration for Meteora DBC
 * 
 * This module defines the fee structure for all DBC pools created via MemeLaunch.
 * Fee settings are baked into the on-chain DBC config key and cannot be modified
 * from the frontend - they are set when the config is created.
 * 
 * FEE SPLIT ARCHITECTURE:
 * 
 * 1. Trading Fees (Bonding Curve Swaps):
 *    - Total fee is defined in the DBC config's baseFee parameters
 *    - creatorTradingFeePercentage (0-100) defines creator's share
 *    - Partner share = 100 - creatorTradingFeePercentage
 *    - MemeLaunch uses 70/30 split: 70% to treasury (partner), 30% to creator
 * 
 * 2. Migration Fees (When curve completes and migrates to AMM):
 *    - migrationFee.feePercentage: % of liquidity taken as fee
 *    - migrationFee.creatorFeePercentage: creator's share of that fee
 *    - Partner share = 100 - creatorFeePercentage
 *    - MemeLaunch uses 100/0 split: 100% to treasury, 0% to creator
 * 
 * WHO RECEIVES FEES:
 *    - Partner fees: Go to the feeClaimer address set in the config (MemeLaunch treasury)
 *    - Creator fees: Go to the poolCreator address set when the pool was created
 */

import { PublicKey } from '@solana/web3.js';
import { debug } from "@/lib/debug";

// ============================================================================
// MEMELAUNCH TREASURY CONFIGURATION
// ============================================================================

/**
 * MemeLaunch Treasury Wallet
 * 
 * This is the wallet that receives the "partner" share of all fees:
 * - 70% of trading fees from bonding curve swaps
 * - 100% of migration fees when pools complete
 * - Locked LP tokens (partner portion)
 * 
 * This address must match the `feeClaimer` field in the on-chain DBC config.
 */
export const MEMELAUNCH_TREASURY_PUBKEY = new PublicKey(
  '9ynRciWU7m3vGtGmm6s9YRW2avXA5D64AMbcedhakxQL'
);

// ============================================================================
// FEE SPLIT CONFIGURATION
// ============================================================================

/**
 * Creator's percentage of trading fees (0-100)
 * 
 * On the Meteora DBC config, this is stored as `creatorTradingFeePercentage`.
 * - Value of 30 means creator gets 30%, partner (treasury) gets 70%
 * - This is set when the config is created on-chain
 */
export const CREATOR_TRADING_FEE_PERCENTAGE = 30;

/**
 * Partner's (MemeLaunch treasury) percentage of trading fees
 * Calculated as 100 - CREATOR_TRADING_FEE_PERCENTAGE
 */
export const PARTNER_TRADING_FEE_PERCENTAGE = 100 - CREATOR_TRADING_FEE_PERCENTAGE;

/**
 * Creator's percentage of migration fees (0-100)
 * 
 * On the Meteora DBC config, this is stored in `migrationFee.creatorFeePercentage`.
 * - Value of 0 means all migration fees go to treasury
 */
export const CREATOR_MIGRATION_FEE_PERCENTAGE = 0;

/**
 * Partner's (MemeLaunch treasury) percentage of migration fees
 */
export const PARTNER_MIGRATION_FEE_PERCENTAGE = 100 - CREATOR_MIGRATION_FEE_PERCENTAGE;

// ============================================================================
// CONFIG VALIDATION
// ============================================================================

/**
 * Expected fee configuration for MemeLaunch DBC config key
 * Used to validate the on-chain config matches our expected settings
 */
export interface ExpectedFeeConfig {
  feeClaimer: PublicKey;
  creatorTradingFeePercentage: number;
  migrationFeePercentage?: number;
  creatorMigrationFeePercentage?: number;
}

/**
 * Result of config validation
 */
export interface ConfigValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  actualConfig: {
    feeClaimer?: string;
    creatorTradingFeePercentage?: number;
    migrationFeePercentage?: number;
    creatorMigrationFeePercentage?: number;
  };
}

/**
 * Get the expected fee configuration for MemeLaunch
 */
export function getExpectedFeeConfig(): ExpectedFeeConfig {
  return {
    feeClaimer: MEMELAUNCH_TREASURY_PUBKEY,
    creatorTradingFeePercentage: CREATOR_TRADING_FEE_PERCENTAGE,
    migrationFeePercentage: 50, // 50% of liquidity as migration fee (V5 active 2026-05-06; V4 was 10)
    creatorMigrationFeePercentage: CREATOR_MIGRATION_FEE_PERCENTAGE,
  };
}

/**
 * Validate that an on-chain config matches expected MemeLaunch settings
 */
export function validateFeeConfig(
  onChainConfig: {
    feeClaimer?: PublicKey;
    creatorTradingFeePercentage?: number;
    migrationFeePercentage?: number;
    creatorMigrationFeePercentage?: number;
  }
): ConfigValidationResult {
  const expected = getExpectedFeeConfig();
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate feeClaimer
  if (onChainConfig.feeClaimer) {
    if (onChainConfig.feeClaimer.toBase58() !== expected.feeClaimer.toBase58()) {
      errors.push(
        `feeClaimer mismatch: expected ${expected.feeClaimer.toBase58()}, ` +
        `got ${onChainConfig.feeClaimer.toBase58()}`
      );
    }
  } else {
    warnings.push('feeClaimer not found in config');
  }

  // Validate creatorTradingFeePercentage
  if (onChainConfig.creatorTradingFeePercentage !== undefined) {
    if (onChainConfig.creatorTradingFeePercentage !== expected.creatorTradingFeePercentage) {
      errors.push(
        `creatorTradingFeePercentage mismatch: expected ${expected.creatorTradingFeePercentage}, ` +
        `got ${onChainConfig.creatorTradingFeePercentage}`
      );
    }
  } else {
    warnings.push('creatorTradingFeePercentage not found in config');
  }

  // Note: creatorMigrationFeePercentage is set in the on-chain config at creation time
  // A value of 0 means all migration fees go to the partner (treasury)
  // This is informational - the on-chain value determines actual behavior
  if (onChainConfig.creatorMigrationFeePercentage !== undefined &&
      expected.creatorMigrationFeePercentage !== undefined) {
    if (onChainConfig.creatorMigrationFeePercentage !== expected.creatorMigrationFeePercentage) {
      // Log as info in DEV only - the on-chain config is authoritative
      if (import.meta.env.DEV) {
        debug.trace(
          `[FeeConfig] creatorMigrationFeePercentage: on-chain=${onChainConfig.creatorMigrationFeePercentage}, ` +
          `expected=${expected.creatorMigrationFeePercentage} (using on-chain value)`
        );
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    actualConfig: {
      feeClaimer: onChainConfig.feeClaimer?.toBase58(),
      creatorTradingFeePercentage: onChainConfig.creatorTradingFeePercentage,
      migrationFeePercentage: onChainConfig.migrationFeePercentage,
      creatorMigrationFeePercentage: onChainConfig.creatorMigrationFeePercentage,
    },
  };
}

/**
 * Log fee configuration summary for debugging
 * Only logs in development mode
 */
export function logFeeConfigSummary(): void {
  if (import.meta.env.DEV) {
    debug.trace('[MemeLaunch Fee Config] Treasury:', MEMELAUNCH_TREASURY_PUBKEY.toBase58());
    debug.trace('[MemeLaunch Fee Config] Creator/Treasury split:', CREATOR_TRADING_FEE_PERCENTAGE + '/' + PARTNER_TRADING_FEE_PERCENTAGE);
  }
}
